/**
 * Scan Samples, drawn into any container. The shell (with the camera box) is drawn once per session so a
 * running camera survives; the body (location, switches, lists) redraws on every change. The camera box shows only
 * while the camera is open.
 *
 * Colours come from the `--hl-scan-*` variables on `.hl-scan`, which default to the Bulma scheme variables. A host
 * with its own palette sets them, e.g. the panels' dark Scan Samples panel or a workspace tab:
 *
 *     #MyHost .hl-scan { --hl-scan-background: …; --hl-scan-text: …; --hl-scan-text-strong: …; --hl-scan-text-weak: …;
 *         --hl-scan-border: …; --hl-scan-border-weak: …; --hl-scan-head-background: …;
 *         --hl-scan-input-background: …; --hl-scan-input-text: …; --hl-scan-input-border: …; }
 */

const libPictView = require('pict-view');
const libLocationTree = require('../Location-Tree.js');

/**
 * The view's configuration for one cart. The default cart is `SampleScanning`; a second one (the bundle's
 * attachInstance) gets its own provider name and element/template prefix, so both can be on screen at once.
 *
 * @param {string} pProviderHash - the provider the view draws (its buttons reach it through providerRef)
 * @param {string} pPrefix - prefixes the element IDs and template hashes
 * @return {Object}
 */
function buildConfiguration(pProviderHash, pPrefix)
{
	return {
		ViewIdentifier: pPrefix,
		ProviderHash: pProviderHash,
		IDPrefix: pPrefix,
		DefaultRenderable: `${pPrefix}-Shell`,
		DefaultDestinationAddress: `#${pPrefix}-Container`,
		AutoRender: false,
		CSS: /*css*/`
/* The palette; a host overrides these on .hl-scan (see the view's comment). */
.hl-scan {
	--hl-scan-background: var(--bulma-scheme-main);
	--hl-scan-text: var(--bulma-text);
	--hl-scan-text-strong: var(--bulma-text-strong);
	--hl-scan-text-weak: var(--bulma-text-weak);
	--hl-scan-border: var(--bulma-border);
	--hl-scan-border-weak: var(--bulma-border-weak);
	--hl-scan-head-background: var(--bulma-scheme-main-bis);
	--hl-scan-input-background: var(--bulma-scheme-main);
	--hl-scan-input-text: var(--bulma-text-strong);
	--hl-scan-input-border: var(--bulma-border);
}
.hl-scan { background-color: var(--hl-scan-background); color: var(--hl-scan-text); }
.hl-scan .hl-scan-section { padding: 0.75rem; border-bottom: 1px solid var(--hl-scan-border); }
.hl-scan .hl-scan-label { display: block; font-weight: 600; margin-bottom: 0.25rem; color: var(--hl-scan-text-strong); }
.hl-scan .hl-scan-check { display: block; margin-top: 0.5rem; color: var(--hl-scan-text); }
.hl-scan .hl-scan-check:hover { color: var(--hl-scan-text-strong); }
.hl-scan .hl-scan-hint, .hl-scan .hl-scan-muted, .hl-scan .hl-scan-empty { color: var(--hl-scan-text-weak); font-size: 0.8rem; }
.hl-scan .hl-scan-hint { margin-top: 0.25rem; }
.hl-scan .hl-scan-fixed { display: flex; align-items: center; gap: 0.35rem; color: var(--hl-scan-text-strong); }
.hl-scan .hl-scan-hint b, .hl-scan .hl-scan-row b { color: var(--hl-scan-text-strong); }
.hl-scan .hl-scan-empty { padding: 0.75rem; }
.hl-scan .hl-scan-actions { display: flex; gap: 0.5rem; padding: 0.75rem; }
.hl-scan .hl-scan-list-head { display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0.75rem; font-weight: 600; color: var(--hl-scan-text-strong); background-color: var(--hl-scan-head-background); border-bottom: 1px solid var(--hl-scan-border); }
.hl-scan .hl-scan-row { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.75rem; border-bottom: 1px solid var(--hl-scan-border-weak); }
.hl-scan .hl-scan-row-body { flex-grow: 1; min-width: 0; }
.hl-scan .select select { background-color: var(--hl-scan-input-background); color: var(--hl-scan-input-text); border-color: var(--hl-scan-input-border); }
.hl-scan .select:not(.is-multiple):not(.is-loading)::after { border-color: var(--hl-scan-text-weak); }
.hl-scan .hl-scan-camera { max-height: 50vh; overflow-y: auto; border-bottom: 1px solid var(--hl-scan-border); }
.hl-scan .hl-scan-camera.is-hidden { display: none; }
@media screen and (max-width: 768px) { #${pPrefix}-CameraBox__scan_region > video { max-height: 180px; object-fit: cover; } }
`,
		Templates:
		[
			{
				Hash: `${pPrefix}-Shell`,
				Template: /*html*/`
					<div class="hl-scan">
						<div id="${pPrefix}-Settings"></div>
						<div id="${pPrefix}-Camera" class="hl-scan-camera is-hidden"><div id="${pPrefix}-CameraBox"></div></div>
						<div id="${pPrefix}-Lists"></div>
					</div>`,
			},
			{
				Hash: `${pPrefix}-Settings`,
				Template: /*html*/`{~D:Context[0].settingsHTML~}`,
			},
			{
				Hash: `${pPrefix}-Lists`,
				Template: /*html*/`{~D:Context[0].receivedHTML~}{~D:Context[0].collectedHeadHTML~}{~TS:${pPrefix}-Sample-Item:Context[0].collectedSamples~}`,
			},
			{
				Hash: `${pPrefix}-Sample-Item`,
				Template: /*html*/`
				<div class="hl-scan-row">
					{~D:Record._RemoveButton~}
					<div class="hl-scan-row-body">
						<div><b>{~D:Record.Name~}</b> <span class="hl-scan-muted is-size-7">{~D:Record.QRCode~}</span></div>
						<div class="is-size-7 hl-scan-muted">Status: {~D:Record.Status:None~} · Project: {~Entity:Project^Record.IDProject^${pPrefix}-Item-Project^~}</div>
						<div class="is-size-7 hl-scan-muted">Created {~DateFormat:Record.CreateDate^MMMM DD, YYYY - hh:mm a^~} by {~Entity:User^Record.CreatingIDUser^${pPrefix}-Item-User^~}</div>
					</div>
					{~D:Record._PrintButton~}
				</div>`,
			},
			{
				Hash: `${pPrefix}-Item-User`,
				Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
			},
			{
				Hash: `${pPrefix}-Item-Project`,
				Template: /*html*/`{~D:Record.Name~}`,
			},
		],
		Renderables:
		[
			{ RenderableHash: `${pPrefix}-Shell`, TemplateHash: `${pPrefix}-Shell`, RenderMethod: 'replace' },
			{ RenderableHash: `${pPrefix}-Settings`, TemplateHash: `${pPrefix}-Settings`, ContentDestinationAddress: `#${pPrefix}-Settings`, RenderMethod: 'replace' },
			{ RenderableHash: `${pPrefix}-Lists`, TemplateHash: `${pPrefix}-Lists`, ContentDestinationAddress: `#${pPrefix}-Lists`, RenderMethod: 'replace' },
		],
	};
}

const _DefaultConfiguration = buildConfiguration('SampleScanning', 'SampleScanning');

class SampleScanningView extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = pOptions || {};
		const tmpProviderHash = tmpOptions.ProviderHash || 'SampleScanning';
		const tmpPrefix = tmpOptions.IDPrefix || tmpProviderHash;
		// A named cart builds its own templates and element IDs from its provider name and prefix. Those win over the
		// options, which pict has already merged the default cart's configuration into.
		const tmpNamed = (tmpProviderHash !== 'SampleScanning') || (tmpPrefix !== 'SampleScanning');
		super(pFable, tmpNamed ?
			Object.assign({}, tmpOptions, buildConfiguration(tmpProviderHash, tmpPrefix)) :
			Object.assign({}, _DefaultConfiguration, tmpOptions), pServiceHash);
		/** @type {any} */
		this.pict;
	}

	/** @return {any} */
	get scanning()
	{
		return this.pict.providers[this.options.ProviderHash];
	}

	/** @return {string} how the view's buttons reach the provider */
	get providerRef()
	{
		return `_Pict.providers['${this.options.ProviderHash}']`;
	}

	/** @return {string} */
	get prefix()
	{
		return this.options.IDPrefix;
	}

	/**
	 * Draw the whole view into a container; called once per session.
	 * @param {string} [pDestinationAddress]
	 */
	renderShell(pDestinationAddress)
	{
		this.render(`${this.prefix}-Shell`, pDestinationAddress || this.options.DefaultDestinationAddress);
		const tmpCamera = this.scanning && this.scanning.host && this.scanning.host.camera;
		this.showCamera(!!(tmpCamera && tmpCamera.isOpen()));
		this.renderBody();
	}

	/**
	 * Show or hide the camera box: shown only while the camera is open.
	 * @param {boolean} pShown
	 */
	showCamera(pShown)
	{
		if (typeof document === 'undefined')
		{
			return;
		}
		const tmpCamera = document.getElementById(`${this.prefix}-Camera`);
		if (tmpCamera)
		{
			tmpCamera.classList.toggle('is-hidden', !pShown);
		}
	}

	/** Redraw everything except the camera. */
	renderBody()
	{
		if (typeof document !== 'undefined' && !document.getElementById(`${this.prefix}-Settings`))
		{
			return;
		}
		this.render(`${this.prefix}-Settings`);
		// Async, so the sample rows' project and user names are looked up before they draw.
		this.renderAsync(`${this.prefix}-Lists`, () => {});
	}

	/**
	 * @param {any} pValue
	 * @return {string}
	 */
	escapeHTML(pValue)
	{
		return String(pValue ?? '').replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[pChar]));
	}

	/**
	 * The location picker, remember switch, save-each switch, status hint and camera button.
	 * @return {string}
	 */
	get settingsHTML()
	{
		const tmpScanning = this.scanning;
		const tmpLocation = tmpScanning.location;
		const tmpSelectedID = (tmpLocation && tmpLocation.IDLocation) ? String(tmpLocation.IDLocation) : '';
		const tmpOrder = libLocationTree.getTreeOrder(tmpScanning.locations || []);
		if (tmpSelectedID && !tmpOrder.some((pEntry) => String(pEntry.Location.IDLocation) === tmpSelectedID))
		{
			tmpOrder.unshift({ Location: tmpLocation, Depth: 0 });
		}
		const tmpOptions = tmpOrder.map((pEntry) =>
		{
			const tmpID = String(pEntry.Location.IDLocation);
			const tmpIndent = '   '.repeat(pEntry.Depth);
			return `<option value="${this.escapeHTML(tmpID)}"${tmpID === tmpSelectedID ? ' selected' : ''}>${tmpIndent}${this.escapeHTML(pEntry.Location.Name || `Location #${tmpID}`)}</option>`;
		}).join('');
		const tmpPlaceholder = (tmpScanning.locations === null) ? 'Loading locations…' : 'Choose a location, or scan one';
		const tmpStatus = tmpScanning.locationStatus;
		const tmpStatusHint = tmpStatus ? `<p class="hl-scan-hint">Samples moved here are set to <b>${this.escapeHTML(tmpStatus)}</b> unless they are already further along.</p>` : '';
		let tmpCamera = '';
		if (tmpScanning.host.camera)
		{
			const tmpLabel = tmpScanning.host.camera.isOpen() ? 'Close Camera' : 'Open Camera';
			tmpCamera = `<div class="hl-scan-actions"><button type="button" class="button is-primary is-fullwidth" onclick="${this.providerRef}.toggleCamera()">` +
				`<span class="icon mr-2"><i class="fas fa-fw fa-camera-viewfinder"></i></span><span>${tmpLabel}</span></button></div>`;
		}
		// A fixed location (fixLocation) is shown, not offered: no picker and no remember switch.
		const tmpLocationHTML = tmpScanning.fixedLocation ?
			`<span class="hl-scan-label">Location</span>
			<p id="${this.prefix}-Location" class="hl-scan-fixed"><span class="icon"><i class="fas fa-fw fa-location-dot"></i></span><b>${this.escapeHTML(tmpScanning.fixedLocation.Name || `Location #${tmpScanning.fixedLocation.IDLocation}`)}</b></p>
			<p class="hl-scan-hint">Samples are moved into this location.</p>` :
			`<label class="hl-scan-label" for="${this.prefix}-Location">Location</label>
			<div class="select is-fullwidth"><select id="${this.prefix}-Location" onchange="${this.providerRef}.pickLocation(this.value)">
				<option value="">${tmpPlaceholder}</option>${tmpOptions}
			</select></div>
			<label class="checkbox hl-scan-check"><input type="checkbox" ${tmpScanning.rememberLocation ? 'checked' : ''} onchange="${this.providerRef}.setRememberLocation(this.checked)"> Remember this location on this device</label>`;
		return `<div class="hl-scan-section">
			${tmpLocationHTML}
			<label class="checkbox hl-scan-check"><input type="checkbox" ${tmpScanning.saveEachScan ? 'checked' : ''} onchange="${this.providerRef}.setSaveEachScan(this.checked)"> Save each scan right away</label>
			<p class="hl-scan-hint">${tmpScanning.saveEachScan ? 'Each sample you scan is moved to the location as soon as it is scanned.' : 'Scanned samples are collected below; Move all moves them together.'}</p>
			${tmpStatusHint}
		</div>${tmpCamera}`;
	}

	/**
	 * The scans saved straight away, newest first.
	 * @return {string}
	 */
	get receivedHTML()
	{
		const tmpScanning = this.scanning;
		if (!tmpScanning.saveEachScan && tmpScanning.received.length === 0)
		{
			return '';
		}
		const tmpIcons =
		{
			working: '<i class="fas fa-fw fa-spinner fa-spin"></i>',
			ok: '<i class="fas fa-fw fa-circle-check has-text-success"></i>',
			warn: '<i class="fas fa-fw fa-triangle-exclamation has-text-warning"></i>',
			error: '<i class="fas fa-fw fa-circle-xmark has-text-danger"></i>',
			undone: '<i class="fas fa-fw fa-rotate-left"></i>',
		};
		const tmpRows = tmpScanning.received.map((pRow) =>
		{
			const tmpUndo = (pRow.State === 'ok') ? `<button type="button" class="button is-small" onclick="${this.providerRef}.undoReceive(${Number(pRow.Sample.IDSample)})">Undo</button>` : '';
			return `<div class="hl-scan-row">
				<span class="icon">${tmpIcons[pRow.State] || ''}</span>
				<div class="hl-scan-row-body">
					<div><b>${this.escapeHTML(pRow.Sample.Name || 'Sample')}</b> <span class="hl-scan-muted is-size-7">${this.escapeHTML(pRow.Sample.QRCode)}</span></div>
					<div class="is-size-7">${this.escapeHTML(pRow.Message)}</div>
				</div>
				${tmpUndo}
			</div>`;
		}).join('');
		const tmpEmpty = (tmpScanning.received.length === 0) ? '<p class="hl-scan-empty">Samples you scan appear here as they are saved.</p>' : '';
		const tmpFinish = (tmpScanning.received.length > 0) ? `<button type="button" class="button is-small" title="Clears this list; every scan is already saved" onclick="${this.providerRef}.finish()">Finish</button>` : '';
		return `<div class="hl-scan-list-head"><span>Saved</span>${tmpFinish}</div><div>${tmpRows}${tmpEmpty}</div>`;
	}

	/**
	 * The collected list's heading with Clear and Move all.
	 * @return {string}
	 */
	get collectedHeadHTML()
	{
		const tmpScanning = this.scanning;
		const tmpCount = tmpScanning.samples.length;
		if (tmpScanning.saveEachScan && tmpCount === 0)
		{
			return '';
		}
		// Clear leaves kept samples, so with only those there is nothing to clear.
		const tmpClearable = tmpScanning.samples.some((pSample) => !tmpScanning.isKept(pSample.IDSample));
		// Check in and move all is only offered once the cart has a location to move into.
		const tmpCheckInAndMove = tmpScanning.location ?
			`<button type="button" class="button is-small is-link" title="Checks every sample in the list into the lab, then moves them all here" onclick="${this.providerRef}.checkInAndMoveAll()" ${(tmpCount && !tmpScanning.markingReceived) ? '' : 'disabled'}>Check in and move all</button>` :
			'';
		const tmpTarget = tmpScanning.location ? ` to ${this.escapeHTML(tmpScanning.location.Name)}` : '';
		const tmpDisabled = (tmpCount && tmpScanning.location) ? '' : 'disabled';
		const tmpEmpty = (tmpCount === 0) ? '<p class="hl-scan-empty">Scan samples to collect them here.</p>' : '';
		return `<div class="hl-scan-list-head"><span>Collected (${tmpCount})</span>
				<span class="buttons are-small mb-0">
					<button type="button" class="button is-small" title="Removes everything from the list" onclick="${this.providerRef}.clear()" ${tmpClearable ? '' : 'disabled'}>Clear</button>
					<button type="button" class="button is-small is-info" title="Sets every sample in the list to RECV and logs it; they stay in the list" onclick="${this.providerRef}.markReceived()" ${(tmpCount && !tmpScanning.markingReceived) ? '' : 'disabled'}>Check Samples into Lab</button>
					<button type="button" class="button is-small is-success" onclick="${this.providerRef}.moveAll()" ${tmpDisabled}>Move all${tmpTarget}</button>
					${tmpCheckInAndMove}
				</span>
			</div>${tmpEmpty}`;
	}

	/**
	 * The collected samples, each with a remove button (none on a kept sample) and the host's print button when it
	 * prints labels.
	 * @return {Array<Object>}
	 */
	get collectedSamples()
	{
		const tmpScanning = this.scanning;
		const tmpCanPrint = (typeof tmpScanning.host.printLabel === 'function');
		return tmpScanning.samples.map((pSample) => Object.assign({}, pSample,
			{
				_RemoveButton: tmpScanning.isKept(pSample.IDSample) ? '' :
					`<button type="button" class="delete" title="Remove from the list" aria-label="Remove from the list" onclick="${this.providerRef}.removeSample(${Number(pSample.IDSample)})"></button>`,
				_PrintButton: tmpCanPrint ? `<button type="button" title="Print label" class="button is-small" onclick="${this.providerRef}.printLabel(${Number(pSample.IDSample)})"><span class="icon"><i class="fas fa-fw fa-print"></i></span></button>` : '',
			}));
	}
}

module.exports = SampleScanningView;
module.exports.default_configuration = _DefaultConfiguration;
module.exports.buildConfiguration = buildConfiguration;
